import { DateTime } from "luxon";
import { cache, syncState, type CacheStore } from "@/services/cache";
import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@tmi/types/api/fetch";
import type { HasMetaData, NormalizedContent } from "@tmi/types/entities";

import { createDefaultLogger, LogLevels } from "@/types/console-logger";
const logger = createDefaultLogger( LogLevels.DEBUG );

const META_PAGE_SIZE = 5000;
const MAX_CONSECUTIVE_FAILURES = 2;

/** Cooldown applied after a pass that did not complete cleanly, so failures retry quickly. */
const RETRY_THROTTLE_IN_SECONDS = 15;

interface MetaDataFetchResult {
    metadata: HasMetaData[];
    /** False when any page was lost, i.e. the set is a subset of what the server holds. */
    complete: boolean;
}

interface ContentFetchResult {
    itemsUpdated: number;
    /** False when any requested page failed to arrive or failed to persist. */
    complete: boolean;
}

export interface ContentSyncConfig<TResponse, TContentful> {
    name: string;
    store: () => CacheStore< NormalizedContent >;
    syncKey: string;
    metaQueryId: string;
    contentQueryId: string;
    contentPageSize: number;
    metaContentFilter?: Record<string, unknown>;
    extractMetaPayload: ( data: TResponse ) => TContentful[] | undefined | null | void;
    extractContentPayload: ( data: TResponse ) => TContentful[] | undefined | null;
    normalizeItem: ( item: TContentful ) => NormalizedContent | undefined;
}

export class ContentSyncCoordinator<TResponse, TContentful> {
    /**
     * Sync keys with a pass currently running. Coordinators are constructed per sync event, so
     * this has to be shared across instances to be worth anything.
     */
    private static readonly inFlight = new Set<string>();

    readonly #config: ContentSyncConfig<TResponse, TContentful>;

    constructor( config: ContentSyncConfig<TResponse, TContentful> ) {
        this.#config = config;
    }

    /**
     * Bring the local cache in line with the server.
     *
     * The ordering here is load-bearing. It used to be:
     *
     *   1. fetch metadata
     *   2. commit a 5 minute sync lockout
     *   3. delete every stale-or-unknown key
     *   4. fetch the replacement content
     *
     * so anything that went wrong between (3) and (4) — a dropped connection, a 500, the user
     * closing the tab — left the content deleted and sync locked out for five minutes. That is
     * where "my articles disappeared, support told me to reset my data" came from.
     *
     * It now writes before it deletes, deletes only records the server no longer has, and
     * commits the long throttle only once the whole cycle has succeeded.
     */
    async patchRepository( authToken: string ): Promise<number> {
        let itemsUpdated = 0;
        const cfg = this.#config;

        // A slow sync can outlast the supervisor's own cooldown. Without this, two passes over
        // the same store can interleave their reads, writes and deletes.
        if ( ContentSyncCoordinator.inFlight.has( cfg.syncKey ) ) {
            logger.debug( `[ ${ cfg.name }-sync-coordinator ] sync already in flight — skipping` );
            return itemsUpdated;
        }
        ContentSyncCoordinator.inFlight.add( cfg.syncKey );

        try {
            await cache.ready();

            const syncThresholdInMillis = await syncState.get<number>( cfg.syncKey );
            if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {
                logger.debug( `[ ${ cfg.name }-sync-coordinator ] load all available ${ cfg.name } meta-data...` );
                const t0 = performance.now();

                const { metadata: allMetadata, complete: metadataComplete } =
                    await this.refreshMetaDataFromServer( authToken );

                if ( 0 === allMetadata.length ) {
                    await this.scheduleRetry( cfg.syncKey );
                    return itemsUpdated;
                }

                const t1 = performance.now();
                logger.info( `[ ${ cfg.name }-sync-coordinator ] Retrieved & loaded ${ allMetadata.length } ${ cfg.name } meta records in ${ t1 - t0 } ms` );

                const allMetaDataByIdMap = new Map<string, HasMetaData>(
                    allMetadata.map( ( meta: HasMetaData ) => [ meta.contentfulId, meta ] )
                );

                const t2 = performance.now();

                // Keys the server no longer knows about. Held back until the write phase has
                // succeeded — a partial metadata read must never be treated as authoritative
                // evidence that content was deleted upstream.
                const orphanedKeys: string[] = [];

                const store = cfg.store();
                if ( !await store.isEmpty() ) {
                    for ( const [ key, content ] of await store.entries() ) {
                        const meta: HasMetaData | undefined = allMetaDataByIdMap.get( key );
                        if ( typeof meta !== 'undefined' ) {
                            if ( 0 === meta.updatedDate?.localeCompare( content.updatedDate ?? '' ) ) {
                                // Already current — nothing to fetch.
                                allMetaDataByIdMap.delete( key );
                            }
                            // Otherwise it is stale: leave it in the map so it is re-fetched and
                            // overwritten in place. Deleting it first would open a window in
                            // which the user has neither the old copy nor the new one.
                        } else {
                            orphanedKeys.push( key );
                        }
                    }
                }

                const t3 = performance.now();
                logger.info( `[ ${ cfg.name }-sync-coordinator ] Aggregated missing ${ cfg.name } in ${ t3 - t2 } ms` );

                // WRITE PHASE — always before the delete phase.
                let contentComplete = true;
                const missingIds = Array.from( allMetaDataByIdMap.keys() );
                if ( missingIds.length > 0 ) {
                    const result = await this.refreshContentFromServer( authToken, missingIds );
                    itemsUpdated = result.itemsUpdated;
                    contentComplete = result.complete;
                }

                // DELETE PHASE — only against a metadata set we know to be whole, and only once
                // the replacement content is safely persisted.
                if ( metadataComplete && contentComplete ) {
                    if ( orphanedKeys.length > 0 ) {
                        await store.deleteMany( orphanedKeys );
                        logger.info( `[ ${ cfg.name }-sync-coordinator ] Removed ${ orphanedKeys.length } ${ cfg.name } no longer on the server` );
                    }

                    const nextSyncThresholdInMillis = DateTime.utc().plus( { minutes: 5 } ).toMillis();
                    await syncState.put( cfg.syncKey, nextSyncThresholdInMillis );
                } else {
                    logger.warn( `[ ${ cfg.name }-sync-coordinator ] Incomplete sync (metadata: ${ metadataComplete }, content: ${ contentComplete }) — keeping ${ orphanedKeys.length } candidate(s) for removal and retrying shortly` );
                    await this.scheduleRetry( cfg.syncKey );
                }

                const t4 = performance.now();
                logger.info( `[ ${ cfg.name }-sync-coordinator ] Processing all missing ${ cfg.name } took ${ t4 - t0 } ms` );
            }
        } catch ( err: any ) {
            logger.error( `[ ${ cfg.name }-sync-coordinator ] Sync failed:`, err );
        } finally {
            ContentSyncCoordinator.inFlight.delete( cfg.syncKey );
        }

        return itemsUpdated;
    }

    /**
     * Short cooldown used whenever a pass did not complete cleanly, so a transient failure is
     * retried in seconds instead of being locked out for the full success interval.
     */
    private async scheduleRetry( syncKey: string ): Promise<void> {
        const retryThresholdInMillis = DateTime.utc().plus( { seconds: RETRY_THROTTLE_IN_SECONDS } ).toMillis();
        await syncState.put( syncKey, retryThresholdInMillis );
    }

    /**
     * @returns the metadata gathered, and whether every page was retrieved successfully.
     *          `complete: false` means the returned set is a subset of what the server holds —
     *          the caller must not use it to decide that anything was deleted upstream.
     */
    private async refreshMetaDataFromServer( authToken: string ): Promise<MetaDataFetchResult> {
        let page = 0;
        let complete = true;
        const cfg = this.#config;

        const metadata: HasMetaData[] = [];
        let metaPayload: TContentful[] | undefined | null | void = [];

        do {
            const variables: Record<string, unknown> = {
                skip: page,
                limit: META_PAGE_SIZE
            };
            if ( cfg.metaContentFilter ) {
                variables.contentFilter = cfg.metaContentFilter;
            }

            // Retry the SAME page rather than advancing past it. Skipping a page used to drop a
            // whole window of metadata on any transient error, and everything in that window then
            // looked to the caller like content the server had deleted.
            //
            // The retry is an explicit inner loop on purpose: `continue` inside a do/while jumps
            // to the loop CONDITION, so a retry expressed that way would silently fall out of
            // pagination and report a truncated result as complete.
            let response: Awaited<ReturnType<typeof goFetch<TResponse, PersistedQueryPayload>>> | null = null;
            for ( let attempt = 1; attempt <= MAX_CONSECUTIVE_FAILURES; attempt++ ) {
                response = await goFetch<TResponse, PersistedQueryPayload>( {
                    serverBaseUri: `${ import.meta.env.VITE_CONTENT_BASE_URI }`,
                    endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                    payload: {
                        id: cfg.metaQueryId,
                        variables
                    },
                    authToken
                } );

                if ( response ) {
                    break;
                }

                logger.warn( `${ cfg.name } metadata fetch failed (attempt ${ attempt }/${ MAX_CONSECUTIVE_FAILURES } for page ${ page })` );
            }

            if ( !response ) {
                logger.warn( `Too many consecutive failures, stopping ${ cfg.name } metadata pagination` );
                complete = false;
                break;
            }

            metaPayload = cfg.extractMetaPayload( response.data );
            if ( metaPayload ) {
                ( metaPayload as TContentful[] ).forEach( ( item: TContentful ) => {
                    const a = item as any;
                    metadata.push( {
                        contentfulId: a.id,
                        slug: a.slug,
                        updatedDate: a.publish_at
                    } );
                } );
            } else {
                // A response that carries no payload is malformed, not an empty last page — an
                // empty page still arrives as an empty array. Treating it as the end of the data
                // would hand the caller a truncated set flagged as authoritative.
                logger.warn( `[ ${ cfg.name }-sync-coordinator ] metadata page ${ page } carried no payload` );
                complete = false;
                break;
            }

            page++;
        } while ( ( ( metaPayload as any[] | undefined )?.length ?? 0 ) >= META_PAGE_SIZE );

        return { metadata, complete };
    }

    /**
     * @returns how many records were written, and whether every page was retrieved successfully.
     *          `complete: false` means some requested content is still missing locally, so the
     *          caller must not treat the cache as reconciled.
     */
    private async refreshContentFromServer( authToken: string, missingContentIds: string[] ): Promise<ContentFetchResult> {
        const t0 = performance.now();
        let itemsUpdated = 0;
        let consecutiveFailures = 0;
        let complete = true;
        const cfg = this.#config;

        for ( let offset = 0; offset < missingContentIds.length; offset += cfg.contentPageSize ) {
            const idsForPage = missingContentIds.slice( offset, offset + cfg.contentPageSize );

            const response =
                await goFetch<TResponse, PersistedQueryPayload>( {
                    serverBaseUri: `${ import.meta.env.VITE_CONTENT_BASE_URI }`,
                    endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                    payload: {
                        id: cfg.contentQueryId,
                        variables: {
                            skip: 0,
                            limit: cfg.contentPageSize,
                            contentFilter: {
                                ids: idsForPage
                            }
                        }
                    },
                    authToken
                } );

            if ( !response ) {
                consecutiveFailures++;
                complete = false;
                logger.warn( `${ cfg.name } content fetch failed (${ consecutiveFailures }/${ MAX_CONSECUTIVE_FAILURES } consecutive failures)` );

                if ( consecutiveFailures >= MAX_CONSECUTIVE_FAILURES ) {
                    logger.warn( `Too many consecutive failures, stopping ${ cfg.name } content pagination` );
                    break;
                }

                continue;
            }

            consecutiveFailures = 0;

            const contentPayload = cfg.extractContentPayload( response.data );
            if ( null != contentPayload ) {
                const normalized: NormalizedContent[] = [];
                contentPayload.forEach( ( item: TContentful ) => {
                    const result = cfg.normalizeItem( item );
                    if ( typeof result !== 'undefined' ) {
                        normalized.push( result );
                    }
                } );

                const store = cfg.store();
                try {
                    await store.putMany( normalized.map( c => [ c.contentfulId, c ] as const ) );
                    itemsUpdated += normalized.length;
                } catch ( err: any ) {
                    // The write is the whole point of this pass. If it fails, the cache did not
                    // receive its replacements, so the caller must not go on to delete anything.
                    complete = false;
                    logger.error( `[ ${ cfg.name }-sync-coordinator ] Failed to persist ${ cfg.name }:`, err );
                }

                const t1 = performance.now();
                logger.info( `[ ${ cfg.name }-sync-coordinator ] Processing ${ normalized.length } ${ cfg.name } took ${ t1 - t0 } ms` );
            } else {
                complete = false;
                logger.warn( `[ ${ cfg.name }-sync-coordinator ] Response carried no ${ cfg.name } payload` );
            }
        }

        return { itemsUpdated, complete };
    }
}
